import {
  MAX_READING_ID_LENGTH,
  MAX_STARS,
  MAX_UPSERT_BOOKMARKS,
  MIN_STARS,
} from "@bindery/convex-reader/bounds";
import {
  MAX_BOOKMARK_CLOCK_SKEW_MS,
  mergeBookmarkLatestWins,
} from "@bindery/shared/reader/library-merge";
import type { SessionState } from "@/convex/session";
import {
  readMeta,
  readerRange,
  writeMeta,
  type BookmarkRow,
  type BookmarkValue,
  type OutboxBookmarkRow,
  type ReaderDb,
} from "@/storage/db";
import {
  convexErrorKind,
  type SyncBackend,
  type UpsertBookmarksResult,
  type WireBookmark,
} from "./backend";
import {
  createRetryGate,
  systemClock,
  type Clock,
  type SyncLogger,
} from "./retry";

export const CLOCK_SKEW_META_KEY = "clockSkewMs";
// library.upsertBookmarks refuses a lastupdate over 5 min ahead of its own
// clock, so a fast device is corrected well before it gets there.
export const CLOCK_SKEW_THRESHOLD_MS = 60_000;
export const BOOKMARK_FLUSH_DEBOUNCE_MS = 2_000;
// A turn never stamps this close to the server's refusal line, so a future-
// dated row on the device cannot push every later turn past it.
export const BOOKMARK_STAMP_CEILING_MS = MAX_BOOKMARK_CLOCK_SKEW_MS - 60_000;

/** Positive when the device clock is ahead of the Worker's. */
export function clockSkewOf(state: SessionState): number | null {
  return state.status === "ready"
    ? state.receivedAt - state.session.serverTime
    : null;
}

export function stampLastupdate(now: number, skewMs: number): number {
  return Math.abs(skewMs) > CLOCK_SKEW_THRESHOLD_MS ? now - skewMs : now;
}

// What library.upsertBookmarks answers with `bad_bookmark` for the whole
// batch; one such row would block every other reading's bookmark.
export function bookmarkProblem(bookmark: BookmarkValue): string | null {
  if (bookmark.readingId.length > MAX_READING_ID_LENGTH) return "readingId";
  if (!Number.isSafeInteger(bookmark.currentPage) || bookmark.currentPage < 0) {
    return "currentPage";
  }
  if (
    bookmark.stars !== undefined &&
    !(
      Number.isSafeInteger(bookmark.stars) &&
      bookmark.stars >= MIN_STARS &&
      bookmark.stars <= MAX_STARS
    )
  ) {
    return "stars";
  }
  if (!Number.isFinite(bookmark.lastupdate)) return "lastupdate";
  return null;
}

const toWire = (bookmark: BookmarkValue): WireBookmark => ({
  readingId: bookmark.readingId,
  currentPage: BigInt(bookmark.currentPage),
  lastupdate: bookmark.lastupdate,
  ...(bookmark.completed !== undefined && { completed: bookmark.completed }),
  ...(bookmark.stars !== undefined && { stars: BigInt(bookmark.stars) }),
});

const valueOf = (row: BookmarkValue): BookmarkValue => ({
  readingId: row.readingId,
  currentPage: row.currentPage,
  lastupdate: row.lastupdate,
  ...(row.completed !== undefined && { completed: row.completed }),
  ...(row.stars !== undefined && { stars: row.stars }),
});

const supersede = (stored: BookmarkRow | undefined, turn: BookmarkRow) => ({
  ...turn,
  stars: turn.stars ?? stored?.stars,
  completed: turn.completed ?? stored?.completed,
});

export type BookmarkChange = Omit<BookmarkValue, "lastupdate">;

export interface BookmarkSyncDeps {
  db: ReaderDb;
  backend: SyncBackend;
  /** The session's reader: the only one whose rows are sent. */
  currentReaderId: () => string | null;
  /** Whose rows are written and read locally; defaults to the session's. */
  localReaderId?: () => string | null;
  signedOut: (error: unknown) => boolean;
  logger: SyncLogger;
  clock?: Clock;
  /** The token named another reader than the rows': re-read the session. */
  onReaderMismatch?: () => void;
}

export interface BookmarkSync {
  setClockSkew: (skewMs: number) => Promise<void>;
  loadClockSkew: () => Promise<void>;
  /** Writes the mirror now and queues the change; flushes after 2 s. */
  write: (change: BookmarkChange) => Promise<void>;
  flush: () => Promise<void>;
  retryNow: () => Promise<void>;
  /**
   * `library.get`'s bookmarks merged with this device's unsent ones, filed
   * under the reader the server confirmed they belong to.
   */
  mergeServer: (
    readerId: string,
    server: readonly BookmarkValue[],
  ) => Promise<BookmarkRow[]>;
  mirror: () => Promise<BookmarkRow[]>;
  bookmarkFor: (readingId: string) => Promise<BookmarkRow | undefined>;
}

export function createBookmarkSync({
  db,
  backend,
  currentReaderId,
  localReaderId = currentReaderId,
  signedOut,
  logger,
  clock = systemClock,
  onReaderMismatch,
}: BookmarkSyncDeps): BookmarkSync {
  const retry = createRetryGate(clock);
  let skewMs = 0;
  let skewMeasured = false;
  let paused = false;
  let debounce: unknown = null;
  let inFlight: Promise<void> | null = null;
  let flushAgain = false;

  async function quarantine(
    readerId: string,
    bookmark: BookmarkValue,
    field: string,
  ): Promise<void> {
    await db.quarantine.add({
      readerId,
      kind: "bookmark",
      payload: valueOf(bookmark),
      field,
      reason: "bad_bookmark",
      quarantinedAt: clock.now(),
    });
    logger.error(
      "sync.bookmark.quarantine",
      { type: "BadBookmark", message: field, code: "bad_bookmark" },
      { field },
    );
  }

  function scheduleFlush(): void {
    if (debounce !== null) clock.clearTimer(debounce);
    debounce = clock.setTimer(() => {
      debounce = null;
      void flush();
    }, BOOKMARK_FLUSH_DEBOUNCE_MS);
  }

  async function write(change: BookmarkChange): Promise<void> {
    const readerId = localReaderId();
    if (readerId === null) return;
    const bookmark: BookmarkValue = {
      ...change,
      lastupdate: stampLastupdate(clock.now(), skewMs),
    };
    const problem = bookmarkProblem(bookmark);
    if (problem !== null) {
      await quarantine(readerId, bookmark, problem);
      return;
    }
    const key: [string, string] = [readerId, bookmark.readingId];
    await db.transaction("rw", db.bookmarks, db.outboxBookmarks, async () => {
      const [mirrored, queued] = await Promise.all([
        db.bookmarks.get(key),
        db.outboxBookmarks.get(key),
      ]);
      // A turn is the reader's latest act, so it replaces what this device
      // holds and is stamped past it: latest-wins keeps the stored copy on
      // a tie, and a clock set back mid-reading would stamp under the turn
      // already sent.
      const latestHeld = Math.max(
        queued?.lastupdate ?? -Infinity,
        mirrored?.lastupdate ?? -Infinity,
      );
      const ceiling = bookmark.lastupdate + BOOKMARK_STAMP_CEILING_MS;
      const row: BookmarkRow = {
        ...valueOf(bookmark),
        readerId,
        lastupdate: Math.max(
          bookmark.lastupdate,
          Math.min(latestHeld + 1, ceiling),
        ),
      };
      await db.bookmarks.put(supersede(mirrored, row));
      await db.outboxBookmarks.put({
        ...supersede(queued, row),
        queuedAt: queued?.queuedAt ?? clock.now(),
        // bookmarks_full: the server will refuse this reading again.
        state: queued?.state ?? "pending",
      });
    });
    scheduleFlush();
  }

  async function applyResult(
    readerId: string,
    sent: readonly OutboxBookmarkRow[],
    result: UpsertBookmarksResult,
  ): Promise<void> {
    const skipped = new Set(result.skipped.map((entry) => entry.readingId));
    await db.transaction("rw", db.outboxBookmarks, async () => {
      for (const row of sent) {
        const key: [string, string] = [readerId, row.readingId];
        if (skipped.has(row.readingId)) {
          await db.outboxBookmarks.update(key, { state: "skipped" });
          continue;
        }
        const current = await db.outboxBookmarks.get(key);
        if (current?.lastupdate === row.lastupdate) {
          await db.outboxBookmarks.delete(key);
        }
      }
    });
    if (skipped.size > 0) {
      logger.event("sync.bookmark.skipped", {
        count: skipped.size,
        reason: "bookmarks_full",
      });
    }
  }

  async function send(
    readerId: string,
    rows: readonly OutboxBookmarkRow[],
  ): Promise<boolean> {
    try {
      const result = await backend.upsertBookmarks(readerId, rows.map(toWire));
      retry.succeeded();
      await applyResult(readerId, rows, result);
      return true;
    } catch (error) {
      if (signedOut(error)) {
        paused = true;
        return false;
      }
      const kind = convexErrorKind(error);
      if (kind === "invalid_input") return isolate(readerId, rows);
      if (kind === "reader_mismatch") onReaderMismatch?.();
      const delay = retry.failed(() => void flush());
      logger.error("sync.bookmark.error", error, {
        count: rows.length,
        outcome: kind === "reader_mismatch" ? "reader_mismatch" : "backoff",
        retry_in_ms: delay,
      });
      return false;
    }
  }

  // The server throws the whole batch for one bad row and does not say
  // which, so a failed batch is re-sent one bookmark at a time.
  async function isolate(
    readerId: string,
    rows: readonly OutboxBookmarkRow[],
  ): Promise<boolean> {
    const [only] = rows;
    if (rows.length === 1 && only) {
      await quarantine(readerId, only, "batch");
      await db.outboxBookmarks.delete([readerId, only.readingId]);
      return true;
    }
    for (const row of rows) {
      if (!(await send(readerId, [row]))) return false;
    }
    return true;
  }

  async function drain(): Promise<void> {
    const readerId = currentReaderId();
    if (readerId === null || paused || retry.waiting()) return;
    const [lower, upper] = readerRange(readerId);
    const pending = (
      await db.outboxBookmarks
        .where("[readerId+readingId]")
        .between(lower, upper)
        .toArray()
    )
      .filter((row) => row.state === "pending")
      .sort((a, b) => a.queuedAt - b.queuedAt);
    for (let start = 0; start < pending.length; start += MAX_UPSERT_BOOKMARKS) {
      const batch = pending.slice(start, start + MAX_UPSERT_BOOKMARKS);
      if (!(await send(readerId, batch))) return;
    }
  }

  function flush(): Promise<void> {
    if (debounce !== null) {
      clock.clearTimer(debounce);
      debounce = null;
    }
    if (inFlight) {
      flushAgain = true;
      return inFlight;
    }
    inFlight = (async () => {
      try {
        do {
          flushAgain = false;
          await drain();
        } while (flushAgain);
      } finally {
        inFlight = null;
      }
    })();
    return inFlight;
  }

  function mirrorOf(readerId: string): Promise<BookmarkRow[]> {
    const [lower, upper] = readerRange(readerId);
    return db.bookmarks
      .where("[readerId+readingId]")
      .between(lower, upper)
      .toArray();
  }

  async function mirror(): Promise<BookmarkRow[]> {
    const readerId = localReaderId();
    return readerId === null ? [] : mirrorOf(readerId);
  }

  async function mergeServer(
    readerId: string,
    server: readonly BookmarkValue[],
  ): Promise<BookmarkRow[]> {
    await db.transaction("rw", db.bookmarks, db.outboxBookmarks, async () => {
      for (const bookmark of server) {
        const serverRow: BookmarkRow = { ...valueOf(bookmark), readerId };
        const queued = await db.outboxBookmarks.get([
          readerId,
          bookmark.readingId,
        ]);
        const local = queued
          ? mergeBookmarkLatestWins<BookmarkRow>(serverRow, {
              ...valueOf(queued),
              readerId,
            })
          : null;
        await db.bookmarks.put(local ?? serverRow);
      }
    });
    return mirrorOf(readerId);
  }

  return {
    async setClockSkew(next) {
      skewMs = next;
      skewMeasured = true;
      await writeMeta(db, CLOCK_SKEW_META_KEY, next);
    },
    async loadClockSkew() {
      const stored = await readMeta<number>(db, CLOCK_SKEW_META_KEY);
      if (!skewMeasured && stored !== undefined) skewMs = stored;
    },
    write,
    flush,
    retryNow() {
      paused = false;
      retry.reset();
      return flush();
    },
    mergeServer,
    mirror,
    async bookmarkFor(readingId) {
      const readerId = localReaderId();
      if (readerId === null) return undefined;
      return db.bookmarks.get([readerId, readingId]);
    },
  };
}
